import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import Icon from './Icon';
import { OrnDivider } from './Ornaments';
import { WhatsAppGlyph } from './CtaButtons';
import { SEVA_SCHEDULE, MAHA_SEVA, whatsappSevaLink } from '../../lib/constants';
import { track } from '../../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const container = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const bookSeva = (seva) => {
  track('cta_whatsapp', `seva:${seva.name}`);
  window.open(whatsappSevaLink(seva), '_blank', 'noopener,noreferrer');
};

const SeatsPill = ({ left }) => {
  if (left === undefined || left === null) return null;
  const tone =
    left <= 20
      ? 'text-red-600 border-red-200 bg-red-50'
      : left <= 45
      ? 'text-amber-700 border-amber-200 bg-amber-50'
      : 'text-emerald-700 border-emerald-200 bg-emerald-50';
  return (
    <span
      data-testid="seats-pill"
      className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone}`}
    >
      <Users className="h-3 w-3" />
      {left <= 20 ? `Only ${left} seats left!` : `${left} of 100 seats left`}
    </span>
  );
};

const SevaSchedule = () => {
  const [avail, setAvail] = useState({});

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/seva-availability`)
      .then((r) => alive && setAvail(r.data.availability || {}))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      id="seva-schedule"
      data-testid="seva-schedule-section"
      className="scroll-mt-24 bg-[linear-gradient(180deg,#F7EFD9,#FDFBF7)] py-16 md:py-20"
    >
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <OrnDivider className="mb-5" />
          <h2 className="font-serif text-3xl tracking-tight text-ink md:text-4xl lg:text-5xl">
            Seva Schedule & Offerings
          </h2>
          <p className="mx-auto mt-4 max-w-xl font-sans text-base text-ink/65">
            Only 100 seats per seva — tap any seva to reserve it instantly on WhatsApp for the
            Gurupurnima Sai Baba Mahotsava, 29th July 2026.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {SEVA_SCHEDULE.map((s) => (
            <motion.button
              key={s.name}
              type="button"
              variants={item}
              whileHover={{ y: -4 }}
              onClick={() => bookSeva(s)}
              data-testid={`seva-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
              className="group flex items-center gap-4 rounded-2xl border border-gold/25 bg-white p-4 text-left shadow-[0_18px_40px_-38px_rgba(92,22,22,0.55)] transition-colors hover:border-gold/60 md:p-5"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-[radial-gradient(circle_at_50%_35%,#FBF3D8,#FFFFFF)] text-gold-deep transition-transform duration-300 group-hover:scale-110">
                <Icon name={s.icon} className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-serif text-lg leading-tight text-ink">{s.name}</h3>
                <p className="mt-0.5 font-sans text-xs text-ink/55">{s.time}</p>
                <SeatsPill left={avail[s.name]} />
              </div>
              <div className="shrink-0 text-right">
                {s.price ? (
                  <span className="font-serif text-xl text-gold-deep">₹{s.price}</span>
                ) : (
                  <span className="font-sans text-xs font-medium text-ink/55">{s.note}</span>
                )}
                <span className="mt-1 flex items-center justify-end gap-1 text-[11px] font-medium text-whatsapp opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <WhatsAppGlyph className="h-3 w-3" /> Book
                </span>
              </div>
            </motion.button>
          ))}
        </motion.div>

        {/* Maha Seva highlight */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          data-testid="seva-maha-seva"
          className="relative mt-6 overflow-hidden rounded-2xl border border-gold/40 bg-maroon p-6 text-ivory md:p-8"
        >
          <div className="velvet-noise pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay" />
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gold/10 blur-2xl" />
          <div className="relative flex flex-col items-center gap-5 text-center sm:flex-row sm:justify-between sm:text-left">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-gold/50 text-gold">
                <Icon name={MAHA_SEVA.icon} className="h-6 w-6" strokeWidth={1.6} />
              </div>
              <div>
                <h3 className="font-serif text-2xl text-gold">{MAHA_SEVA.displayName || MAHA_SEVA.name}</h3>
                <p className="mt-1 font-sans text-sm text-ivory/75">{MAHA_SEVA.desc}</p>
                {avail['Maha Seva (All Sevas)'] !== undefined && (
                  <p className="mt-1.5 inline-flex items-center gap-1 font-sans text-xs font-medium text-gold" data-testid="maha-seats">
                    <Users className="h-3 w-3" /> Only {avail['Maha Seva (All Sevas)']} of 100 seats left
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-center gap-3 sm:items-end">
              <span className="font-serif text-3xl text-ivory">₹{MAHA_SEVA.price}</span>
              <button
                type="button"
                data-testid="maha-seva-book"
                onClick={() => bookSeva(MAHA_SEVA)}
                className="inline-flex items-center gap-2.5 rounded-full bg-whatsapp px-6 py-3 font-sans text-xs font-semibold uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5"
              >
                <WhatsAppGlyph className="h-4 w-4" /> Book on WhatsApp
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SevaSchedule;
